import { RadioGroupField } from '@components/radioButton/RadioGroupField';
import { Box, CardHeader, Typography, Grid, Theme } from '@mui/material';
import { useStore } from '@store/GlobalStore';
import { adjustFontSize } from '@theme/styleOverrides/typography';
import { useUpdateUser } from '@services/user/useUpdateUser';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { PREFERENCE_CONFIGS } from './preferenceConfigs';
import { UserPatchRequestBody } from '@lio/api-types';

interface PreferenceFieldProps {
  config: (typeof PREFERENCE_CONFIGS)[keyof typeof PREFERENCE_CONFIGS];
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

const PreferenceField = ({ config, value, disabled, onChange }: PreferenceFieldProps) => (
  <Grid container sx={commonStyles.gridContainer}>
    <RadioGroupField
      value={value}
      data-testid={config.testId}
      inputLabel={config.label}
      disabled={disabled}
      options={config.options}
      sx={commonStyles.radioGroup}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
    />
  </Grid>
);

export const LayoutPreferences = () => {
  const [layoutPreferences, currentUser] = useStore((state) => [state.layoutPreferences, state.currentUser], shallow);

  const { updateUser, isPending } = useUpdateUser({ isEditingOwnProfile: true });
  const { enqueueSnackbar } = useSnackbar();

  const { offeringGridColumns, fontSize, offeringColorMode } = layoutPreferences;

  const saveDisplayPreferences = useCallback(
    async (newPreferences: Partial<typeof layoutPreferences>) => {
      if (!currentUser?.id) {
        enqueueSnackbar('User not found', { variant: 'error' });
        return;
      }

      try {
        const requestBody: Partial<UserPatchRequestBody> = {
          displayPreferences: {
            ...(currentUser?.displayPreferences && typeof currentUser.displayPreferences === 'object'
              ? currentUser.displayPreferences
              : {}),
            ...newPreferences,
          } as Record<string, unknown>,
        };

        await updateUser({
          requestBody: requestBody as UserPatchRequestBody,
          requestParams: { userId: currentUser.id },
        });
      } catch (error) {
        console.error('Failed to save display preferences:', error);
        const message = await getApiErrorMessage(error, 'Failed to save display preferences');
        enqueueSnackbar(message, { variant: 'error' });
      }
    },
    [currentUser, updateUser, enqueueSnackbar],
  );

  const handlePreferenceChange = useCallback(
    (key: keyof typeof layoutPreferences) => (value: string) => {
      saveDisplayPreferences({ [key]: value } as Partial<typeof layoutPreferences>);
    },
    [saveDisplayPreferences],
  );

  const preferenceFields = useMemo(
    () => [
      {
        config: PREFERENCE_CONFIGS.offeringGridColumns,
        value: offeringGridColumns,
        onChange: handlePreferenceChange('offeringGridColumns'),
      },
      {
        config: PREFERENCE_CONFIGS.fontSize,
        value: fontSize,
        onChange: handlePreferenceChange('fontSize'),
      },
      {
        config: PREFERENCE_CONFIGS.offeringColorMode,
        value: offeringColorMode,
        onChange: handlePreferenceChange('offeringColorMode'),
      },
    ],
    [offeringGridColumns, fontSize, offeringColorMode, handlePreferenceChange],
  );

  return (
    <Box sx={{ height: `100%`, overflowY: 'auto', backgroundColor: 'drawerNavigation.main' }}>
      <CardHeader
        title={
          <Box>
            <Typography
              variant='h6'
              sx={{ fontSize: (theme: Theme) => `${adjustFontSize(18, theme.palette.fontSize)}px` }}
            >
              Display Preferences
            </Typography>
            <Typography
              variant='bodySmall'
              display='block'
              color='text.subdued'
              sx={{
                mt: 1,
                fontStyle: 'italic',
              }}
            >
              These features will only affect this page.
            </Typography>

            {preferenceFields.map(({ config, value, onChange }) => (
              <PreferenceField
                key={config.key}
                config={config}
                value={value}
                disabled={isPending}
                onChange={onChange}
              />
            ))}
          </Box>
        }
      />
    </Box>
  );
};

const commonStyles = {
  radioGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  gridContainer: {
    mt: 2,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    mt: 1,
  },
} as const;
